/**
 * What the user is told when a background Brain update ends. Pure, so the
 * wording for each outcome (including the honest "could not confirm") is tested.
 */
import type { IndexingJobSpec, IndexingOutcome } from './brainIndexingJob';

export type NoticeLevel = 'success' | 'error' | 'warning';

export interface IndexingNotice {
  level: NoticeLevel;
  title: string;
  description: string;
}

/** The message shown right after the save, while the update runs in the background. */
export function startedMessage(organizationName: string): { title: string; description: string } {
  return {
    title: `Estamos actualizando el Brain de ${organizationName}`,
    description: 'Te avisaremos con el resultado en cuanto termine. Mientras tanto puedes seguir trabajando.',
  };
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

function subject(spec: IndexingJobSpec): string {
  return spec.paths.length > 1 ? `«${spec.label}» (${plural(spec.paths.length, 'documento', 'documentos')})` : `«${spec.label}»`;
}

export function describeOutcome(spec: IndexingJobSpec, outcome: IndexingOutcome, organizationName: string): IndexingNotice {
  const removing = spec.action === 'deindex';

  if (outcome.kind === 'failed') {
    return {
      level: 'error',
      title: `No se pudo actualizar el Brain de ${organizationName}`,
      description:
        `${outcome.errors.join(' · ')}. ` +
        (removing
          ? `${subject(spec)} ya está eliminado; solo falta quitarlo de la búsqueda semántica y del grafo.`
          : `${subject(spec)} está guardado; solo la búsqueda semántica y el grafo no se actualizaron esta vez.`),
    };
  }

  if (outcome.kind === 'unconfirmed') {
    return {
      level: 'warning',
      title: `El Brain de ${organizationName} sigue actualizándose`,
      description:
        `No hemos podido confirmar el resultado de ${subject(spec)}. ` +
        (removing
          ? 'Puede tardar unos minutos más en reflejarse en la búsqueda.'
          : 'Si no aparece en la búsqueda en unos minutos, vuelve a guardarlo.'),
    };
  }

  if (removing) {
    return {
      level: 'success',
      title: `Brain de ${organizationName} actualizado`,
      description: `${subject(spec)} ya no forma parte de la memoria del Brain.`,
    };
  }

  const stats = [
    outcome.chunks !== undefined ? plural(outcome.chunks, 'fragmento', 'fragmentos') : null,
    outcome.entities !== undefined ? plural(outcome.entities, 'entidad', 'entidades') : null,
    outcome.relationships !== undefined ? plural(outcome.relationships, 'conexión', 'conexiones') : null,
  ].filter((part): part is string => part !== null);

  return {
    level: 'success',
    title: `Brain de ${organizationName} actualizado`,
    description: `${subject(spec)} ya es buscable${stats.length > 0 ? ` · ${stats.join(' · ')}` : ''}.`,
  };
}
